import { t } from 'i18next';
import { useEffect } from 'react';

import AdvancedFilters from 'components/desktop/Ingredient/Search/Components/AdvancedFilters/AdvancedFilters';
import SearchResultList from 'components/desktop/Ingredient/Search/Components/SearchResultList/SearchResultList';
import { useIngredientSearchUrl } from 'components/desktop/Ingredient/Search/useIngredientSearchUrl';
import useSearchState from 'components/desktop/Ingredient/Search/useSearchState';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import { usePermissions } from 'hooks/usePermissions';
import { useReaction } from 'hooks/useReaction';
import { ISelectItem } from 'interfaces/ISelectItem';
import * as IngredientSearchResponse from 'types/Ingredient/Search/IngredientSearchResponseDto';
import * as Search from 'types/Ingredient/Search/Search';

const maxNumberOfButtons = 10;

const IngredientSearchComponent = () => {
	const { isIngredientEditor } = usePermissions();
	const { search, searchHandlers } = useIngredientSearchUrl();
	const { state, handlers } = useSearchState(search);
	const [ingredientResponse, isLoaded] = useReaction<IngredientSearchResponse.Type, Search.Type>(
		IngredientSearchResponse.create(),
		IngredientSearchResponse.getFromApi,
		search
	);

	useEffect(() => {
		if (window.location.href.includes('?')) openAdvancedSearch();
	}, []);

	const getSuggestedIngredients = async (): Promise<ISelectItem[]> => {
		return IngredientSearchResponse.getFromApiSW(state.searchTerm, search.showOnlyDeleted);
	};

	const openAdvancedSearch = () => {
		const advancedSearchButton = document.getElementById('advancedSearchButton');
		if (advancedSearchButton) advancedSearchButton.click();
	};

	return (
		<main className="container">
			<div className="d-flex justify-content-between">
				<RenderIf condition={!search.showOnlyDeleted}>
					<h1>{t('ingredient:SEARCH_INGREDIENT')}</h1>
				</RenderIf>
				<RenderIf condition={search.showOnlyDeleted}>
					<h1>{t('ingredient:INGREDIENT_ARCHIVE')}</h1>
				</RenderIf>
				<RenderIf condition={isIngredientEditor}>
					<BtnNew link={'/ingredient/new'} name={t('ingredient:CREATE_INGREDIENT')} />
				</RenderIf>
			</div>
			<br />
			<div className="row">
				<div className="col-lg-7">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedIngredients}
							handleSearch={(input) =>
								searchHandlers.changeSearchTerm(input ? input : state.searchTerm)
							}
							searchTerm={state.searchTerm}
							setSearchTerm={handlers.setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-2">
					<button
						id="advancedSearchButton"
						className="btn btn-primary"
						type="button"
						data-bs-toggle="collapse"
						data-bs-target="#advancedSearch"
						aria-expanded="true"
						aria-controls="advancedSearch"
					>
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={searchHandlers.changePageSize}
							currentValue={String(search.pageSize)}
						/>
						<DropdownSortOrder
							type={'ingredient'}
							listFunction={searchHandlers.changeSortOrder}
							currentValue={search.sortOrder}
						/>
					</div>
				</div>
			</div>
			<div className={'collapse'} id="advancedSearch">
				<div className="card card-body mb-3">
					<AdvancedFilters
						disabled={ingredientResponse.totalCount === 0}
						handleChangeAllergens={searchHandlers.changeAllergens}
						handleChangeBaseIngredient={searchHandlers.changeBaseIngredient}
						handleChangeCategories={searchHandlers.changeCategories}
						handleChangeCharacteristics={searchHandlers.changeCharacteristics}
						handleChangeCo2Labels={searchHandlers.changeCo2Labels}
						handleChangeNutriScoreLabels={searchHandlers.changeNutriScoreLabels}
						handleChangeSeasons={searchHandlers.changeSeasons}
						handleChangeSliders={searchHandlers.changeSlider}
						handleChangeTags={searchHandlers.changeTags}
						handleReset={searchHandlers.reset}
						search={search}
						searchResponse={ingredientResponse}
					/>
				</div>
			</div>
			<div className="mb-3">
				<RenderIf condition={isLoaded}>
					<SearchResultList
						archive={search.showOnlyDeleted}
						ingredientList={ingredientResponse.data}
						handleArchiveIngredient={searchHandlers.archive}
						handleRestoreIngredient={searchHandlers.restore}
					/>
				</RenderIf>
				<RenderIf condition={!isLoaded}>
					<LoadingAnimation isLoading={!isLoaded} />
				</RenderIf>
			</div>
			<Pagination
				countOfAllListItems={ingredientResponse.totalCount}
				pageIndex={search.pageIndex}
				setPageIndex={searchHandlers.changePageIndex}
				listItemAmount={search.pageSize}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default IngredientSearchComponent;
